import { Box, Group, Text } from "@mantine/core";
import { Map as OlMap, View } from "ol";
import { defaults as defaultControls } from "ol/control";
import GeoJSON from "ol/format/GeoJSON";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import OSM from "ol/source/OSM";
import VectorSource from "ol/source/Vector";
import { Fill, Stroke, Style, Text as OlText } from "ol/style";
import { useEffect, useRef } from "react";

import { fmtNum } from "./ui";

const RAMP = ["#edf8e9", "#c7e9c0", "#a1d99b", "#74c476", "#41ab5d", "#238b45", "#005a32"];

/** Kartogram po opštinama (GeoJSON iz /analytics/choropleth); klik na opštinu -> onSelect(code). */
export function ChoroplethMap({ data, height = 380, onSelect, selected }: {
  data?: { features: { properties: { code: string; name: string; value: number | null } }[]; label?: string };
  height?: number; onSelect?: (code: string) => void; selected?: string | null;
}) {
  const el = useRef<HTMLDivElement>(null);
  const mapRef = useRef<OlMap | null>(null);
  const srcRef = useRef(new VectorSource());
  const max = Math.max(1e-9, ...(data?.features ?? []).map((f) => Number(f.properties.value) || 0));

  useEffect(() => {
    if (!el.current) return;
    const vl = new VectorLayer({
      source: srcRef.current,
      style: (f) => {
        const v = Number(f.get("value")) || 0;
        const c = RAMP[Math.min(RAMP.length - 1, Math.floor((v / max) * (RAMP.length - 1)))];
        const sel = f.get("code") === selected;
        return new Style({
          fill: new Fill({ color: c + "cc" }), stroke: new Stroke({ color: sel ? "#e8590c" : "#1f5135", width: sel ? 3 : 1 }),
          text: new OlText({ text: `${f.get("name")}\n${f.get("value") == null ? "—" : fmtNum(v, 1)}`, font: "11px 'Manrope Variable', sans-serif", overflow: false,
            fill: new Fill({ color: "#212529" }), stroke: new Stroke({ color: "#fff", width: 3 }) }),
        });
      },
    });
    const map = new OlMap({ target: el.current, controls: defaultControls({ attribution: false }),
      layers: [new TileLayer({ source: new OSM(), opacity: 0.5 }), vl], view: new View({ center: [1930000, 5580000], zoom: 8 }) });
    map.on("click", (e) => {
      const f = map.forEachFeatureAtPixel(e.pixel, (x) => x);
      if (f && onSelect) onSelect(String(f.get("code")));
    });
    mapRef.current = map;
    return () => { map.setTarget(undefined); mapRef.current = null; };
  }, [max, selected]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const src = srcRef.current;
    src.clear();
    if (!data?.features?.length) return;
    src.addFeatures(new GeoJSON().readFeatures(data, { featureProjection: "EPSG:3857" }));
    const ext = src.getExtent();
    if (mapRef.current && ext && Number.isFinite(ext[0])) mapRef.current.getView().fit(ext, { padding: [20, 20, 20, 20], maxZoom: 11 });
  }, [data, max, selected]);

  return (
    <Box>
      <div ref={el} style={{ height, borderRadius: 8, overflow: "hidden" }} />
      <Group gap={4} mt={4} justify="center">
        <Text size="xs" c="dimmed">0</Text>
        {RAMP.map((c) => <Box key={c} w={22} h={8} bg={c} />)}
        <Text size="xs" c="dimmed">{fmtNum(max, 1)} {data?.label ?? ""}</Text>
      </Group>
    </Box>
  );
}
